import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import { useI18n } from "../i18n/I18nProvider";
import { useAuth } from "../context/AuthContext";
import { useCart } from "../context/CartContext";
import PromoBanner from "./PromoBanner";
import ProductCard from "./ProductCard";
import CartDrawer from "./CartDrawer";
import LoginModal from "./LoginModal";
import CheckoutModal from "./CheckoutModal";
import MobileCategoryLanding from "./MobileCategoryLanding";
import MobileCategoryBrowse from "./MobileCategoryBrowse";
import DesktopCategoryLanding from "./DesktopCategoryLanding";
import { buildParents } from "../lib/parentCategories";
import { labelFor } from "../lib/categoryMeta";
import { Flame, Sparkles, ArrowLeft } from "lucide-react";
import { toast } from "sonner";

const Shop = () => {
    const { t } = useI18n();
    const { isAuthed } = useAuth();
    const { setOpen: setCartOpen } = useCart();

    const [categories, setCategories] = useState([]);
    const [products, setProducts] = useState([]);
    // Filter state is mirrored to URL search params (?c=&sub=) so the browser's
    // Back / Forward buttons navigate between landing ↔ category views, and
    // category URLs are shareable. Read from URL on every render so the back
    // button takes effect automatically.
    const [searchParams, setSearchParams] = useSearchParams();
    const filter    = searchParams.get("c")   || "all";
    const subFilter = searchParams.get("sub") || "all";

    const updateFilter = useCallback((nextFilter, nextSub = "all") => {
        const params = {};
        if (nextFilter && nextFilter !== "all") params.c = nextFilter;
        if (nextSub && nextSub !== "all")       params.sub = nextSub;
        // Push so each navigation creates a history entry (back button friendly)
        setSearchParams(params, { replace: false });
    }, [setSearchParams]);

    const setFilter = (next) => {
        // Switching parent always resets sub-filter
        updateFilter(typeof next === "function" ? next(filter) : next, "all");
    };
    const setSubFilter = (next) => {
        updateFilter(filter, typeof next === "function" ? next(subFilter) : next);
    };

    const [loading, setLoading] = useState(true);

    // Mobile view ("landing" vs "browse") is derived from the URL so that the
    // back button on a category page automatically pops to the landing grid.
    const mobileView = filter === "all" ? "landing" : "browse";
    const [loginOpen, setLoginOpen] = useState(false);
    const [checkoutOpen, setCheckoutOpen] = useState(false);
    const [pendingCheckout, setPendingCheckout] = useState(false);

    useEffect(() => {
        let cancelled = false;
        async function load() {
            setLoading(true);
            try {
                const [c, p] = await Promise.all([
                    api.get("/categories"),
                    api.get("/products"),
                ]);
                if (cancelled) return;
                setCategories(c.data.categories || []);
                setProducts(p.data.products || []);
            } catch (e) {
                if (process.env.NODE_ENV !== "production") console.error(e);
            } finally {
                if (!cancelled) setLoading(false);
            }
        }
        load();
        return () => { cancelled = true; };
    }, []);

    useEffect(() => {
        const handleHome = () => {
            setSearchParams({}, { replace: false });
        };
        window.addEventListener("bazaar:home", handleHome);
        return () => window.removeEventListener("bazaar:home", handleHome);
    }, [setSearchParams]);

    useEffect(() => {
        const p = new URLSearchParams(window.location.search);
        if (p.has("stripe_session") || p.has("stripe_cancel")) {
            setCheckoutOpen(true);
        }
    }, []);

    const parents = useMemo(() => buildParents(categories), [categories]);
    const activeParent = useMemo(
        () => parents.find((p) => p.id === filter) || null,
        [parents, filter],
    );

    // Sub-filter is automatically cleared whenever the parent changes since
    // setFilter() resets it via updateFilter().

    const visibleProducts = useMemo(() => {
        if (filter === "all" || !activeParent) return products;
        const slugs = subFilter === "all"
            ? activeParent.child_slugs
            : [subFilter];
        const set = new Set(slugs);
        return products.filter((p) => set.has(p.category));
    }, [products, filter, subFilter, activeParent]);

    const hotDeals = useMemo(() => {
        return visibleProducts
            .filter((p) => p.original_all > p.price_all)
            .sort((a, b) => {
                const da = 1 - a.price_all / a.original_all;
                const db = 1 - b.price_all / b.original_all;
                return db - da;
            })
            .slice(0, 8);
    }, [visibleProducts]);

    const handleCheckoutClick = () => {
        setCartOpen(false);
        if (!isAuthed) {
            setPendingCheckout(true);
            setLoginOpen(true);
            toast(t("toast_login_required"));
            return;
        }
        setCheckoutOpen(true);
    };

    const onLoginSuccess = () => {
        if (pendingCheckout) {
            setPendingCheckout(false);
            setCheckoutOpen(true);
        }
    };

    const handleMobileParentSelect = (parentId) => {
        setFilter(parentId);
        window.scrollTo({ top: 0, behavior: "smooth" });
    };

    // Use the browser's own history so back button works identically to
    // navigating through the in-app Back arrow.
    const handleMobileBack = () => {
        window.history.back();
    };

    const goBackToCategories = () => {
        window.history.back();
        window.scrollTo({ top: 0, behavior: "smooth" });
    };

    const parentLabel = (p) => {
        const tr = t(p.name_key);
        return tr && tr !== p.name_key ? tr : p.label;
    };

    const handleDisabledParentClick = (label) =>
        toast(`${label}: ${t("category_coming_soon")}`);

    // ────────────────────────────────────────────────────────────────
    // Desktop content
    // ────────────────────────────────────────────────────────────────
    const desktopContent = (
        <div className="hidden md:block" data-testid="desktop-shop">
            <main className="max-w-[1320px] mx-auto px-5 md:px-8 py-8 md:py-10">
                {loading ? (
                    <div className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8 xl:grid-cols-12 gap-2 md:gap-3">
                        {["s1","s2","s3","s4","s5","s6","s7","s8","s9","s10","s11","s12","s13","s14","s15","s16","s17","s18","s19","s20","s21","s22","s23","s24"].map((k) => (
                            <div
                                key={k}
                                className="bg-white rounded-2xl border border-neutral-200/70 overflow-hidden animate-pulse"
                            >
                                <div className="aspect-square bg-neutral-100" />
                                <div className="p-4 space-y-3">
                                    <div className="h-3 bg-neutral-100 rounded w-3/4" />
                                    <div className="h-3 bg-neutral-100 rounded w-1/2" />
                                    <div className="h-6 bg-neutral-100 rounded w-1/3 mt-3" />
                                </div>
                            </div>
                        ))}
                    </div>
                ) : filter === "all" ? (
                    <DesktopCategoryLanding
                        parents={parents}
                        onSelect={setFilter}
                        onDisabledClick={handleDisabledParentClick}
                    />
                ) : (
                    <>
                        <div className="flex items-center justify-between mb-5">
                            <div className="inline-flex items-center gap-3">
                                <button
                                    type="button"
                                    onClick={goBackToCategories}
                                    data-testid="desktop-back-to-categories"
                                    className="h-9 w-9 rounded-full bg-neutral-100 hover:bg-neutral-200 flex items-center justify-center"
                                    aria-label="Back to categories"
                                >
                                    <ArrowLeft className="h-4 w-4" />
                                </button>
                                <span className="h-9 w-9 rounded-xl bg-neutral-900 text-white flex items-center justify-center text-lg">
                                    {activeParent?.icon || "🛒"}
                                </span>
                                <h2
                                    data-testid="section-title"
                                    className="font-display text-2xl md:text-3xl font-medium tracking-tight"
                                >
                                    {activeParent ? parentLabel(activeParent) : t("section_all_products")}
                                </h2>
                            </div>
                            <span className="text-xs md:text-sm text-neutral-500 font-medium">
                                {visibleProducts.length} {t("filter_all").toLowerCase()}
                            </span>
                        </div>

                        {/* Sub-category chips — only when the parent has >1 child slug */}
                        {activeParent && activeParent.children.length > 1 && (
                            <div
                                data-testid="sub-filter-row"
                                className="mb-6 flex gap-2 overflow-x-auto no-scrollbar pb-1"
                            >
                                <button
                                    type="button"
                                    onClick={() => setSubFilter("all")}
                                    data-testid="sub-filter-all"
                                    className={`h-9 px-4 rounded-full text-xs font-semibold whitespace-nowrap shrink-0 transition-colors ${
                                        subFilter === "all"
                                            ? "bg-[#FF5722] text-white"
                                            : "bg-neutral-100 text-neutral-700 hover:bg-neutral-200"
                                    }`}
                                >
                                    {t("sub_filter_all")} · {activeParent.items}
                                </button>
                                {activeParent.children.map((c) => (
                                <button
                                    key={c.id}
                                    type="button"
                                    onClick={() => setSubFilter(c.id)}
                                    data-testid={`sub-filter-${c.id}`}
                                    className={`h-9 px-4 rounded-full text-xs font-semibold whitespace-nowrap shrink-0 transition-colors ${
                                        subFilter === c.id
                                            ? "bg-[#FF5722] text-white"
                                            : "bg-neutral-100 text-neutral-700 hover:bg-neutral-200"
                                    }`}
                                >
                                    <span>{labelFor(c, t)}</span> · {c.items}
                                </button>
                                ))}
                            </div>
                        )}

                        {hotDeals.length > 0 && (
                            <section data-testid="hot-deals-section" className="mb-10">
                                <div className="flex items-center justify-between mb-5">
                                    <div className="inline-flex items-center gap-2.5">
                                        <span className="h-9 w-9 rounded-xl bg-[#FF5722] text-white flex items-center justify-center">
                                            <Flame className="h-5 w-5" />
                                        </span>
                                        <h2 className="font-display text-2xl md:text-3xl font-medium tracking-tight">
                                            {t("section_hot_deals")}
                                        </h2>
                                    </div>
                                    <span className="text-xs md:text-sm text-neutral-500 font-medium">
                                        {hotDeals.length} {t("filter_all").toLowerCase()}
                                    </span>
                                </div>
                                <div className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8 xl:grid-cols-12 gap-2 md:gap-3">
                                    {hotDeals.map((p) => (
                                        <ProductCard key={`hot-${p.id}`} product={p} />
                                    ))}
                                </div>
                            </section>
                        )}

                        <section data-testid="all-products-section">
                            <div className="flex items-center gap-2.5 mb-5">
                                <span className="h-9 w-9 rounded-xl bg-neutral-900 text-white flex items-center justify-center">
                                    <Sparkles className="h-5 w-5" />
                                </span>
                                <h2 className="font-display text-xl md:text-2xl font-medium tracking-tight">
                                    {t("section_all_products")}
                                </h2>
                            </div>
                            {visibleProducts.length === 0 ? (
                                <div className="bg-white border border-neutral-200/70 rounded-3xl p-12 text-center text-neutral-500">
                                    {t("section_no_products")}
                                </div>
                            ) : (
                                <div className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8 xl:grid-cols-12 gap-2 md:gap-3">
                                    {visibleProducts.map((p) => (
                                        <ProductCard key={p.id} product={p} />
                                    ))}
                                </div>
                            )}
                        </section>
                    </>
                )}
            </main>
        </div>
    );

    // ────────────────────────────────────────────────────────────────
    // Mobile content
    // ────────────────────────────────────────────────────────────────
    const mobileContent = (
        <div className="md:hidden" data-testid="mobile-shop">
            {loading ? (
                <div className="px-4 py-6 grid grid-cols-2 gap-3">
                    {["m1","m2","m3","m4","m5","m6","m7","m8"].map((k) => (
                        <div
                            key={k}
                            className="aspect-square bg-neutral-100 rounded-2xl animate-pulse"
                        />
                    ))}
                </div>
            ) : mobileView === "landing" ? (
                <MobileCategoryLanding
                    parents={parents}
                    onSelect={handleMobileParentSelect}
                    onDisabledClick={handleDisabledParentClick}
                />
            ) : (
                <MobileCategoryBrowse
                    parents={parents}
                    activeId={filter}
                    onSelect={(pid) => {
                        setFilter(pid);
                        window.scrollTo({ top: 0, behavior: "smooth" });
                    }}
                    onDisabledClick={handleDisabledParentClick}
                    activeParent={activeParent}
                    subFilter={subFilter}
                    onSubFilterChange={setSubFilter}
                    products={visibleProducts}
                    onBack={handleMobileBack}
                />
            )}
        </div>
    );

    return (
        <>
            <PromoBanner />
            {desktopContent}
            {mobileContent}

            <CartDrawer onCheckout={handleCheckoutClick} />
            <LoginModal
                open={loginOpen}
                onClose={() => { setLoginOpen(false); setPendingCheckout(false); }}
                onSuccess={onLoginSuccess}
            />
            <CheckoutModal open={checkoutOpen} onClose={() => setCheckoutOpen(false)} />
        </>
    );
};

export default Shop;
